import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import HeaderBar from '@/components/HeaderBar';
import AccountSidebar from '@/components/AccountSidebar';
import { AlertTriangle, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DangerPage({ token, user, logout }) {
    const navigate = useNavigate();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') {
            toast.error('Please type DELETE to confirm');
            return;
        }

        setDeleting(true);
        try {
            await axios.delete(`${API}/api/auth/account`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Account deleted successfully');
            logout();
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to delete account';
            toast.error(message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="settings-page">
            <HeaderBar user={user} logout={logout} />

            <div className="settings-header">
                <div className="settings-header-content settings-header-row">
                    <h1 className="settings-title">Danger Zone</h1>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/')}
                        className="back-button"
                    >
                        ← Back to dashboard
                    </Button>
                </div>
            </div>

            <div className="settings-container">
                <AccountSidebar token={token} />

                <div className="settings-main">
                    <div className="settings-content">
                        {/* Danger Zone Hero Header */}
                        <div className="subscription-hero subscription-hero--danger">
                            <div className="subscription-hero__icon">
                                <AlertTriangle className="w-5 h-5" />
                            </div>
                            <div className="subscription-hero__text">
                                <h2 className="subscription-hero__title">Danger zone</h2>
                                <p className="subscription-hero__subtitle">Actions in this section are irreversible. Please proceed with caution.</p>
                            </div>
                        </div>

                        {/* Delete Account Section */}
                        <div className="pref-section">
                            <div className="pref-section__header">
                                <div className="pref-section__icon pref-section__icon--danger">
                                    <Trash2 className="w-4 h-4" />
                                </div>
                                <div className="pref-section__heading">
                                    <h3 className="pref-section__title">Delete account</h3>
                                    <p className="pref-section__subtitle">Permanently delete your account and all associated data. This action cannot be undone.</p>
                                </div>
                            </div>

                            {!showDeleteConfirm ? (
                                <Button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="danger-trigger-btn"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete my account
                                </Button>
                            ) : (
                                <div className="danger-confirm">
                                    <p className="danger-confirm__intro">This will permanently delete:</p>
                                    <ul className="danger-confirm__list">
                                        <li>Your account and profile</li>
                                        <li>All your websites and tracking data</li>
                                        <li>All analytics data and history</li>
                                        <li>Your subscription and billing history</li>
                                    </ul>
                                    <label className="danger-confirm__label">
                                        Type <span className="danger-confirm__word">DELETE</span> to confirm:
                                    </label>
                                    <input
                                        type="text"
                                        value={deleteConfirmText}
                                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                                        placeholder="Type DELETE"
                                        className="danger-confirm__input"
                                    />
                                    <div className="danger-confirm__actions">
                                        <Button
                                            onClick={handleDeleteAccount}
                                            disabled={deleting || deleteConfirmText !== 'DELETE'}
                                            className="danger-confirm__delete-btn"
                                        >
                                            {deleting ? 'Deleting...' : 'Permanently delete account'}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setShowDeleteConfirm(false);
                                                setDeleteConfirmText('');
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
